class videoTestimonialsComponent extends HTMLElement {
  /** @type {any | null} */
  swiperInstance = null;

  /** @type {IntersectionObserver | null} */
  intersectionObserver = null;

  /** @type {AbortController | null} */
  abort = null;

  /** @type {number | null} */
  rafId = null;

  /** @type {boolean} */
  manualMode = false;

  /** @type {boolean} */
  inViewport = true;

  /** @type {boolean} */
  navTransitioning = false;

  /** @type {number | null} */
  originalSlideCount = null;

  /** @type {MediaQueryList | null} */
  _mediaQuery = null;

  /** @type {() => void} */
  _mediaQueryListener = () => {};

  /** @type {number | null} */
  autoplayTimer = null;

  /** @type {number} */
  currentVideoIndex = 0;

  /** @type {boolean} */
  isUserInteracted = false;

  /** @type {boolean} */
  isCarouselEnabled = true;

  /** @returns {boolean} */
  isMobileViewport() {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
  }

  /** @returns {string} */
  getAutoplayMode() {
    return this.dataset.autoplayInterval || '5000';
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  /**
   * @param {HTMLElement} paginationEl
   * @param {number} slideCount
   * @param {any} swiper
   */
  createCustomPagination(paginationEl, slideCount, swiper) {
    if (!paginationEl) return;

    paginationEl.innerHTML = '';

    for (let i = 0; i < slideCount; i++) {
      const bullet = document.createElement('button');
      bullet.className = 'swiper-pagination-bullet';
      bullet.setAttribute('data-index', String(i));
      bullet.setAttribute('aria-label', `Go to slide ${i + 1}`);

      bullet.addEventListener('click', () => {
        const realIndex = swiper.realIndex % slideCount;
        const targetIndex = i;
        const diff = targetIndex - realIndex;
        if (diff === 0) return;

        this.manualMode = true;
        this.stopTimedAutoplay();

        if (typeof swiper.slideToLoop === 'function') {
          if (Math.abs(diff) <= slideCount / 2) {
            swiper.slideToLoop(targetIndex);
          } else {
            if (diff > 0) swiper.slideToLoop(targetIndex);
            else swiper.slideToLoop(targetIndex + slideCount);
          }
        } else {
          swiper.slideTo(targetIndex);
        }
      });

      paginationEl.appendChild(bullet);
    }

    this.updateCustomPagination(paginationEl, slideCount, swiper);
  }

  /**
   * @param {HTMLElement} paginationEl
   * @param {number} slideCount
   * @param {any} swiper
   */
  updateCustomPagination(paginationEl, slideCount, swiper) {
    if (!paginationEl) return;
    const bullets = paginationEl.querySelectorAll('.swiper-pagination-bullet');
    const realIndex = this.currentVideoIndex;

    bullets.forEach((bullet, index) => {
      if (!(bullet instanceof HTMLElement)) return;
      if (index === realIndex) bullet.classList.add('swiper-pagination-bullet-active');
      else bullet.classList.remove('swiper-pagination-bullet-active');
    });
  }

  init() {
    this.destroy();

    const swiperEl = this.querySelector('.professional-testimonials__swiper');
    if (!(swiperEl instanceof HTMLElement)) return;

    const wrapper = swiperEl.querySelector('.professional-testimonials__swiper-wrapper, .swiper-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    wrapper.querySelectorAll('.slide-clone').forEach((el) => el.remove());
    const slides = Array.from(wrapper.querySelectorAll('.swiper-slide'));
    const slideCount = slides.length;
    this.originalSlideCount = slideCount;
    const isDesktopView = window.innerWidth > 768;
    this.isCarouselEnabled = !(slideCount <= 4 && isDesktopView);

    const navPrev = this.querySelector('.professional-testimonials__nav--prev');
    const navNext = this.querySelector('.professional-testimonials__nav--next');
    const counterEl = this.querySelector('.professional-testimonials__counter');
    const paginationEl = this.querySelector('.professional-testimonials__pagination');
    const pagination = paginationEl instanceof HTMLElement ? paginationEl : undefined;

    if (navPrev instanceof HTMLButtonElement) {
      navPrev.disabled = !this.isCarouselEnabled;
    }
    if (navNext instanceof HTMLButtonElement) {
      navNext.disabled = !this.isCarouselEnabled;
    }

    if (slideCount <= 1) {
      if (navPrev instanceof HTMLElement) navPrev.setAttribute('hidden', '');
      if (navNext instanceof HTMLElement) navNext.setAttribute('hidden', '');
    }

    this.abort = new AbortController();
    const opts = { signal: this.abort.signal };

    if (this.isCarouselEnabled && navPrev instanceof HTMLElement) {
      navPrev.addEventListener(
        'click',
        () => {
          if (this.navTransitioning) return;
          this.manualMode = true;
          this.stopTimedAutoplay();
          this.navTransitioning = true;
        },
        opts
      );
    }

    if (this.isCarouselEnabled && navNext instanceof HTMLElement) {
      navNext.addEventListener(
        'click',
        () => {
          if (this.navTransitioning) return;
          this.manualMode = true;
          this.stopTimedAutoplay();
          this.navTransitioning = true;
        },
        opts
      );
    }

    const carousel = this.querySelector('.professional-testimonials__carousel');
    const SwiperCtor = /** @type {any} */ (window).Swiper;

    const markCarouselReady = () => {
      if (carousel instanceof HTMLElement) {
        carousel.classList.add('professional-testimonials__carousel--ready');
      }
      if (wrapper.hasAttribute('hidden')) {
        wrapper.removeAttribute('hidden');
      }
    };

    // If carousel disabled (≤5 items desktop), show items statically
    if (!this.isCarouselEnabled) {
      if (carousel instanceof HTMLElement) {
        carousel.classList.add('professional-testimonials__carousel--static');
      }
      markCarouselReady();
      if (navPrev instanceof HTMLElement) navPrev.setAttribute('hidden', '');
      if (navNext instanceof HTMLElement) navNext.setAttribute('hidden', '');
      this.setupVideoControls(opts);
      this.setupViewportObserver();
      this.setupBreakpointListener();
      requestAnimationFrame(() => {
        if (this.inViewport) this.playVideoAtIndex(0);
      });
      return;
    }

    if (!SwiperCtor) {
      markCarouselReady();
      return;
    }

    // Manually clone slides so there are enough to fill viewport edges during loop
    if (slideCount > 1) {
      const slideWidth = 337 + 16;
      const minTotalSlides = Math.ceil((window.innerWidth * 3) / slideWidth);
      const clonesNeeded = Math.max(2, Math.ceil(minTotalSlides / slideCount) - 1);
      for (let i = 0; i < clonesNeeded; i++) {
        slides.forEach((slide) => {
          const clone = slide.cloneNode(true);
          if (clone instanceof HTMLElement) clone.classList.add('slide-clone');
          wrapper.appendChild(clone);
        });
      }
    }
    const totalSlides = wrapper.querySelectorAll('.swiper-slide').length;

    const hasNavButtons = navPrev instanceof HTMLElement && navNext instanceof HTMLElement;
    const enableLoop = slideCount > 1;

    this.swiperInstance = new SwiperCtor(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 16,
      centeredSlides: true,
      centeredSlidesBounds: false,
      slideToClickedSlide: true,
      loop: enableLoop,
      loopedSlides: totalSlides,
      speed: 800,
      cssEase: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      watchOverflow: false,
      initialSlide: 0,
      resistanceRatio: 0.85,
      grabCursor: true,
      navigation: {
        enabled: hasNavButtons,
        prevEl: hasNavButtons ? navPrev : null,
        nextEl: hasNavButtons ? navNext : null,
      },
      breakpoints: {
        769: {
          spaceBetween: 32,
        },
        1200: {
          spaceBetween:32,
        },
      },
      on: {
        init: (/** @type {any} */ swiper) => {
          this.manualMode = false;
          this.currentVideoIndex = 0;
          this.updateCounter(counterEl instanceof HTMLElement ? counterEl : null, swiper);
          if (pagination) {
            this.createCustomPagination(pagination, slideCount, swiper);
          }

          if (enableLoop && typeof swiper.slideToLoop === 'function') {
            swiper.slideToLoop(0, 0, false);
          }

          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              markCarouselReady();
            });
          });
        },
        slideChange: (/** @type {any} */ swiper) => {
          this.currentVideoIndex = swiper.realIndex % this.getOriginalSlideCount();
          this.updateCounter(counterEl instanceof HTMLElement ? counterEl : null, swiper);
          if (pagination) this.updateCustomPagination(pagination, slideCount, swiper);
          this.handleSliderChange();
        },
        transitionEnd: () => {
          this.navTransitioning = false;
        },
        touchStart: () => {
          this.manualMode = true;
          this.stopTimedAutoplay();
        },
      },
    });

    this.setupVideoControls(opts);
    this.setupViewportObserver();
    this.setupBreakpointListener();

    requestAnimationFrame(() => {
      if (this.inViewport) {
        this.playVideoAtIndex(0);
      }
    });
  }

  setupBreakpointListener() {
    if (typeof window === 'undefined') return;
    this._mediaQuery?.removeEventListener('change', this._mediaQueryListener);
    this._mediaQuery = window.matchMedia('(max-width: 768px)');
    this._mediaQueryListener = () => this.init();
    this._mediaQuery.addEventListener('change', this._mediaQueryListener);
  }

  /**
   * @param {HTMLElement | null} counterEl
   * @param {any} swiper
   */
  updateCounter(counterEl, swiper) {
    if (!(counterEl instanceof HTMLElement)) return;
    const total = this.getOriginalSlideCount();
    const current = (this.currentVideoIndex % total) + 1;
    const format = (/** @type {number} */ n) => String(n).padStart(2, '0');
    counterEl.textContent = `${format(current)}/${format(total)}`;
  }

  /** @returns {number} */
  getOriginalSlideCount() {
    if (this.originalSlideCount != null) return this.originalSlideCount;
    const swiperEl = this.querySelector('.professional-testimonials__swiper');
    if (!(swiperEl instanceof HTMLElement)) return 0;
    const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide:not(.slide-clone):not(.swiper-slide-duplicate)'));
    return slides.length;
  }

  /**
   * @param {AbortController | { signal: AbortSignal }} opts
   */
  setupControlButtonHandlers(opts) {
    this.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;

        const muteButton = event.target.closest('.professional-testimonials__control--mute');
        const playButton = event.target.closest('.professional-testimonials__control--play');
        if (!(muteButton instanceof HTMLElement) && !(playButton instanceof HTMLElement)) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const slide = event.target.closest('.professional-testimonials__slide, .swiper-slide');
        if (!(slide instanceof HTMLElement)) return;

        const video = slide.querySelector('video');
        if (!(video instanceof HTMLVideoElement)) return;

        this.manualMode = true;
        this.isUserInteracted = true;
        this.stopTimedAutoplay();

        if (muteButton instanceof HTMLElement) {
          const nextMuted = !video.muted;
          video.muted = nextMuted;
          muteButton.setAttribute('aria-label', nextMuted ? 'Unmute' : 'Mute');
          return;
        }

        if (playButton instanceof HTMLElement) {
          if (video.paused) {
            if (this.isMobileViewport()) this.pauseAllVideosExcept(video);
            this.playVideo(video);
          } else {
            video.dataset.playRequested = 'false';
            video.pause();
          }
          playButton.setAttribute('aria-label', video.paused ? 'Play' : 'Pause');
        }
      },
      { signal: opts.signal, capture: true }
    );
  }

  /**
   * @param {AbortController | { signal: AbortSignal }} opts
   */
  setupVideoControls(opts) {
    const slides = Array.from(this.querySelectorAll('.professional-testimonials__slide'));

    this.setupControlButtonHandlers(opts);

    for (const slide of slides) {
      const video = slide.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) continue;

      const muteButton = slide.querySelector('.professional-testimonials__control--mute');
      const playButton = slide.querySelector('.professional-testimonials__control--play');

      if (muteButton instanceof HTMLElement) {
        muteButton.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
      }

      if (playButton instanceof HTMLElement) {
        playButton.setAttribute('aria-label', video.paused ? 'Play' : 'Pause');
      }

      video.addEventListener(
        'ended',
        () => {
          const mode = this.getAutoplayMode();
          if (mode === 'end') {
            // Remove the isUserInteracted check so it advances automatically
            this.advanceSequence();
          }
        },
        opts
      );

      video.addEventListener(
        'play',
        () => {
          if (playButton instanceof HTMLElement) playButton.setAttribute('aria-label', 'Pause');
        },
        opts
      );

      video.addEventListener(
        'pause',
        () => {
          if (playButton instanceof HTMLElement) playButton.setAttribute('aria-label', 'Play');
        },
        opts
      );
      
      video.addEventListener(
        'volumechange',
        () => {
          // Only treat this as user interaction after a manual action.
          if (this.manualMode && !video.muted && !this.isUserInteracted) {
            this.isUserInteracted = true;
            this.stopTimedAutoplay();
          }
        },
        opts
      );
    }
  }

  handleSliderChange() {
    clearTimeout(this._sliderChangeTimeout);
    this._sliderChangeTimeout = setTimeout(() => {
      if (!this.inViewport) return;
      // Pause all videos first
      this.pauseAllVideos();
      // Then play only the current active video
      this.playVideoAtIndex(this.currentVideoIndex, true);
    }, 50);
  }

  /**
   * @param {number} index
   * @param {boolean} [manual]
   */
  playVideoAtIndex(index, manual = false) {
    
    // Find the slide corresponding to the current index
    const swiperEl = this.querySelector('.professional-testimonials__swiper');
    if (!swiperEl) {
        console.warn(`[Testimonials] playVideoAtIndex - No swiper element found!`);
        return;
    }
    
    // First try to find the active slide if it matches the current index
    let slideToPlay = null;
    let videoToPlay = null;
    
    if (this.swiperInstance && this.swiperInstance.slides && this.swiperInstance.slides[this.swiperInstance.activeIndex]) {
        const targetViaSwiper = this.swiperInstance.slides[this.swiperInstance.activeIndex];
        if (targetViaSwiper.dataset.slideIndex === String(index)) {
            slideToPlay = targetViaSwiper;
        }
    }
    
    if (!slideToPlay) {
       const activeSlide = swiperEl.querySelector('.swiper-slide-active');
       if (activeSlide instanceof HTMLElement && activeSlide.dataset.slideIndex === String(index)) {
         slideToPlay = activeSlide;
       }
    }
    
    if (!slideToPlay) {
      // If not active slide, try to find canonical slide with this index
      slideToPlay = swiperEl.querySelector(`.swiper-slide:not(.swiper-slide-duplicate)[data-slide-index="${index}"]`);
      if (slideToPlay) {
      }
    }

    if (slideToPlay) {
      videoToPlay = slideToPlay.querySelector('video');
    }
    
    if (videoToPlay) {
      this.pauseAllVideosExcept(videoToPlay);

      if (videoToPlay.paused) {
        this.playVideo(videoToPlay);
      }
      
      if (!manual && !this.isUserInteracted) {
         this.startTimedAutoplay();
      }
    } else {
      this.pauseAllVideos();
      if (!manual && !this.isUserInteracted) {
         this.startTimedAutoplay();
      }
    }
    
    this.updateActiveCardUI(index);
  }

  /**
   * @param {number} index
   */
  updateActiveCardUI(index) {
     const swiperEl = this.querySelector('.professional-testimonials__swiper');
     if (!swiperEl) return;
     
     const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide'));
     slides.forEach(slide => {
        const slideIndexAttr = slide.getAttribute('data-slide-index');
        const isMatch = slideIndexAttr === String(index);
        
        const card = slide.querySelector('.professional-testimonials__card');
        if (card) {
           if (isMatch) card.classList.add('is-playing');
           else card.classList.remove('is-playing');
        }
     });
  }

  startTimedAutoplay() {
    this.stopTimedAutoplay();
    
    const mode = this.getAutoplayMode();
    if (mode === 'end') return;

    const interval = parseInt(mode, 10) || 5000;
    this.autoplayTimer = window.setTimeout(() => {
      this.advanceSequence();
    }, interval);
  }

  stopTimedAutoplay() {
    if (this.autoplayTimer) {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  /**
   * @param {HTMLVideoElement} video
   */
  playVideo(video) {
    if (!this.isUserInteracted) {
       video.muted = true;
    }
    
    video.dataset.playRequested = 'true';
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.then(() => {
        if (video.dataset.playRequested === 'false') {
          video.pause();
        } else {
        }
      }).catch((error) => {
        if (error.name === 'AbortError') {
           return;
        }
        
        if (video.dataset.playRequested === 'true') {
           video.muted = true;
           video.load();
           const retry = video.play?.();
           if (retry && typeof retry.catch === 'function') {
               retry.catch(e => console.log('[Testimonials] Muted play/retry also failed:', e));
           }
        }
      });
    }
  }

  /**
   * @param {HTMLVideoElement|null} exceptVideo
   */
  pauseAllVideosExcept(exceptVideo) {
    const videos = Array.from(this.querySelectorAll('video'));
    for (const v of videos) {
      if (v instanceof HTMLVideoElement && v !== exceptVideo) {
         v.dataset.playRequested = 'false';
         v.pause();
      }
    }
  }

  pauseAllVideos() {
    this.pauseAllVideosExcept(null);
  }

  playAllVideos() {
    // Only play the active slide's video
    const activeSlide = this.querySelector('.swiper-slide-active');
    if (!(activeSlide instanceof HTMLElement)) return;
    
    const activeVideo = activeSlide.querySelector('video');
    if (!(activeVideo instanceof HTMLVideoElement)) return;
    
    // Pause all other videos
    this.pauseAllVideosExcept(activeVideo);
    
    // Play only the active video
    if (activeVideo.paused) {
      activeVideo.muted = true;
      activeVideo.loop = true;
      activeVideo.setAttribute('playsinline', '');
      const attempt = activeVideo.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {});
      }
    }
  }

  advanceSequence() {
    const total = this.getOriginalSlideCount();
    if (total <= 1) return;
    
    const nextIndex = (this.currentVideoIndex + 1) % total;
    
    // When carousel is disabled (< 5 items on desktop), always loop autoplay
    const loopEnabled = !this.isCarouselEnabled || this.dataset.autoplayLoop !== 'false';
    if (!loopEnabled && nextIndex === 0) {
       this.stopTimedAutoplay();
       return;
    }

    this.currentVideoIndex = nextIndex;

    let actuallySlided = false;
    if (this.swiperInstance) {
       const initialRealIndex = this.swiperInstance.realIndex;
       const isActuallyLooping = this.swiperInstance.params.loop;
       
       if (isActuallyLooping) {
           this.swiperInstance.slideNext();
           actuallySlided = true;
       } else {
           this.swiperInstance.slideTo(this.currentVideoIndex);
       }
       // If slideTo didn't do anything because we're already "displaying" it
       actuallySlided = actuallySlided || (this.swiperInstance.animating || this.swiperInstance.realIndex !== initialRealIndex);
    }
    
    if (!actuallySlided) {
       // manually update pagination UI since swiper slideChange won't fire
       const counterEl = this.querySelector('.professional-testimonials__counter');
       const paginationEl = this.querySelector('.professional-testimonials__pagination');
       
       if (counterEl instanceof HTMLElement) {
          const format = (/** @type {number} */ n) => String(n).padStart(2, '0');
          counterEl.textContent = `${format(this.currentVideoIndex + 1)}/${format(total)}`;
       }
       
       if (paginationEl instanceof HTMLElement) {
           const bullets = paginationEl.querySelectorAll('.swiper-pagination-bullet');
           bullets.forEach((bullet, index) => {
              if (index === this.currentVideoIndex) bullet.classList.add('swiper-pagination-bullet-active');
              else bullet.classList.remove('swiper-pagination-bullet-active');
           });
       }

       this.handleSliderChange();
    }
  }

  setupViewportObserver() {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const isVisible = Boolean(entry && entry.isIntersecting);
        this.inViewport = isVisible;

        if (!isVisible) {
          this.pauseAllVideos();
          this.stopTimedAutoplay();
          return;
        }

        if (this.swiperInstance && typeof this.swiperInstance.update === 'function') {
          this.swiperInstance.update();
        }

        // Always play only the active video
        this.playVideoAtIndex(this.currentVideoIndex);
      },
      { threshold: 0.35 }
    );

    this.intersectionObserver.observe(this);
  }

  startProgressLoop() {
    // left unchanged, not used directly in autoplay but good to keep
    this.stopProgressLoop();

    const progressEl = this.querySelector('.professional-testimonials__progress-bar');
    if (!(progressEl instanceof HTMLElement)) return;

    const activeSlide = this.querySelector('.professional-testimonials__slide.swiper-slide-active');
    if (!(activeSlide instanceof HTMLElement)) return;

    const video = activeSlide.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    const tick = () => {
      if (video.duration && !Number.isNaN(video.duration)) {
        const pct = Math.max(0, Math.min(1, video.currentTime / video.duration));
        progressEl.style.width = `${pct * 100}%`;
      } else {
        progressEl.style.width = '0%';
      }

      this.rafId = requestAnimationFrame(tick);
    };

    this.rafId = requestAnimationFrame(tick);
  }

  stopProgressLoop() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  /**
   * @param {HTMLElement | null} progressEl
   */
  resetProgress(progressEl) {
    if (progressEl instanceof HTMLElement) progressEl.style.width = '0%';
  }

  destroy() {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;

    this.abort?.abort();
    this.abort = null;

    this.stopProgressLoop();
    this.stopTimedAutoplay();

    this._mediaQuery?.removeEventListener('change', this._mediaQueryListener);
    this._mediaQuery = null;

    const carousel = this.querySelector('.professional-testimonials__carousel');
    if (carousel) {
      carousel.classList.remove('professional-testimonials__carousel--ready');
      carousel.classList.remove('professional-testimonials__carousel--static');
    }

    const swiperEl = this.querySelector('.professional-testimonials__swiper');
    const destroyWrapper = swiperEl?.querySelector('.swiper-wrapper');
    if (destroyWrapper) destroyWrapper.querySelectorAll('.slide-clone').forEach((el) => el.remove());

    if (this.swiperInstance && typeof this.swiperInstance.destroy === 'function') {
      this.swiperInstance.destroy(true, true);
    }

    this.swiperInstance = null;
    this.originalSlideCount = null;
  }
}

if (!customElements.get('video-testimonials-component')) {
  customElements.define('video-testimonials-component', videoTestimonialsComponent);
}
